import { useCallback } from "react";

import OfflineVideoPlayer from "../../../components/meeting-status-layouts/offline-video-player";
import { Video } from "../../../components/video-element";
import { useGlobal } from "../../../contexts/globalContext";
import useDidMountEffect from "../../../hooks/useDidMountEffect";
import PlayerControllers from "../player-controllers";

import FnCanvas from "./canvas";
import FnSeekbar from "./seekbar";

const Player = ({ offlineVideo = false, macLikeAnimationState, openFullscreen }) => {
  const { GRID_STATE, SET_GRID_STATE, SCREEN_REF, SET_SCREEN_STATE, MEDIA_TRACKS, SCREEN_STATE } = useGlobal();

  // cellRatio, columnCount, rowCount, containerWidth, containerHeight
  const calcSurface = (r, col, row, w, h) => {
    if ((r * col) / row > w / h) return h * ((h / row) * col * r);
    return w * (((w / col) * row) / r);
  };

  // cellCount, containerWidth, containerHeight
  const res2matrix = (n, w, h) => {
    let row = 1;
    let col = 1;
    const ratio = 4 / 3; // CellRatio
    for (let i = 1; i <= n; i++) {
      if (col * row > i) continue; // If we have un-filled tiles => skip
      if (calcSurface(col + 1, row, w, h) > calcSurface(col, row + 1, w, h)) row += 1;
      else col += 1;
    }

    if ((ratio * col) / row < w / h) return [(ratio * h) / row, h / row]; // Use full height
    return [w / col, w / col / ratio]; // Use full width
  };

  const handleCellClick = (e) => {
    const el = e.currentTarget;
    const index = +el.dataset.index;
    const nextScreenId = index + (index === 0 ? 1 : 0);

    SET_SCREEN_STATE((p) => ({
      ...p,
      currentScreen: {
        ...p.currentScreen,
        id: nextScreenId,
        state: "switching",
      },
    }));

    const clone = document.querySelector(".clone");
    const video = clone.querySelector("video");
    const stream = new MediaStream();

    stream.addTrack(SCREEN_REF.current.tracks[index].track);
    video.srcObject = stream;

    const rect = el.getBoundingClientRect();
    clone.style.top = `${rect.top}px`;
    clone.style.left = `${rect.left}px`;
    clone.style.width = `${rect.width}px`;
    clone.style.height = `${rect.height}px`;
    clone.dataset.index = index;

    setTimeout(() => {
      clone.style.opacity = 1;
      clone.classList.add("animate");
    }, 800);

    setTimeout(() => {
      SET_GRID_STATE((p) => ({
        ...p,
        isActive: !p.isActive,
      }));

      clone.style.opacity = 0;
      clone.classList.remove("animate");
    }, 2000);
  };

  const getMargin = function (width, marginPercentage) {
    return width - (marginPercentage === 0 ? 0 : width === 0 ? 0 : width - (width * marginPercentage) / 100);
  };

  const getGrid = () => {
    const tracks = [];
    Object.keys(MEDIA_TRACKS).forEach((x) => tracks.push({ track: MEDIA_TRACKS[x] }));
    SCREEN_REF.current.tracks = tracks;
    const n = SCREEN_REF.current.tracks.length;
    const [w, h] = res2matrix(n, window.innerWidth - getMargin(window.innerWidth, 10), window.innerHeight);

    const cells = [];

    for (let i = 0; i < n; i++) {
      const item = SCREEN_REF.current.tracks[i];
      const stream = new MediaStream();
      stream.addTrack(item.track);

      cells.push(
        <div key={i} className="screen-grid-cell" data-index={i} style={{ margin: "16px", width: w, height: h }} onClick={handleCellClick}>
          <Video srcObject={stream} autoPlay muted />
        </div>,
      );
    }

    cells.push(
      <div key={n + 1} className="clone animate" style={{ width: 0, height: 0, top: 0, left: 0, opacity: 0 }}>
        <video autoPlay muted />
      </div>,
    );

    return <div className="screen-grid-container">{cells}</div>;
  };

  useDidMountEffect(() => {
    if (GRID_STATE.isActive) {
      const clone = document.querySelector(".clone");

      clone.style.opacity = 0;
      clone.style.opacity = 1;
      clone.classList.add("animate");

      setTimeout(() => {
        clone.classList.remove("animate");
      }, 800);

      setTimeout(() => {
        clone.style.opacity = 0;
        clone.style.top = 0;
        clone.style.left = 0;
        clone.style.width = 0;
        clone.style.height = 0;
      }, 1200);

      setTimeout(() => {
        document.querySelector("#canvas-area").classList.add("hide");
        document.querySelector(".screen-grid-container").classList.add("active");
      }, 400);
    } else {
      document.querySelector("#canvas-area").classList.remove("hide");
      document.querySelector(".screen-grid-container").classList.remove("active");
    }
  }, [GRID_STATE.isActive]);

  const renderIncomingVideo = useCallback(() => <FnCanvas />, [SCREEN_STATE.currentScreen]);

  return (
    <>
      <>
        <audio className="hide" id="audio__video-conf" controls />
        <audio className="hide" id="audio__screen" />
      </>

      <div className="merged-canvas" id="merged-canvas">
        {offlineVideo ? <OfflineVideoPlayer /> : renderIncomingVideo()}
        <FnSeekbar openFullscreen={openFullscreen} />
        {getGrid()}
        <canvas id="canvas-preview"> Canvas is not supported</canvas>
      </div>

      {macLikeAnimationState && <PlayerControllers />}
    </>
  );
};

export default Player;
